import React, {useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useStationsQuery} from "../query";
import {getCoreRowModel, useReactTable} from "@tanstack/react-table";
import DataTable from "../../../../components/DataTable";
import useDataTable from "../../../../components/DataTable/useDataTable";
import StationCode from "../../../../components/columns/StationCode";
import StationCol from "../../../../components/columns/StationCol";
import {formatFilterForAPI} from "../useFeaturesState";
import Icon from "@mui/material/Icon";
import MDBadge from "../../../../materialDashboard/components/MDBadge";
import FeatureSelectionChips from "../Panels/FeatureSelectionChips";
import MDBox from "../../../../materialDashboard/components/MDBox";

const FlagIcon = (flag) => {
    return flag ? <Icon color="success">check_circle</Icon> : <Icon color="error">block</Icon>
}

const ActiveStatus = (props) => {
    const isActive = !Boolean(props.station.deactivatedAt);
    const isActiveContent = isActive ? "active" : "not active";
    const isActiveColor = isActive ? "success" : "error";

    return <MDBadge badgeContent={isActiveContent} color={isActiveColor} variant="gradient" size="sm" />
}

const StationsTable = ({layers, featuresState}) => {
    const {t} = useTranslation(["webgis", "enums"]);
    const {stationSelectedState} = featuresState;
    const {selectedFeatures, handleFeatureDeSelect} = stationSelectedState;

    const {pagination, setPagination, sorting, onSortingChange, fetchDataOptions, goToFirstPage} = useDataTable();

    const params = useMemo(() => ({
        ...formatFilterForAPI.stations(layers),
        id__in: selectedFeatures.length > 0 ? selectedFeatures.map(f => f.properties.id) : undefined,
        ...fetchDataOptions
    }), [layers, fetchDataOptions, selectedFeatures]);

    const {data, isLoading} = useStationsQuery(params);
    const rows = data?.rows ?? [];
    const pageCount = data?.page_count ?? -1;

    useEffect(() => {
        goToFirstPage();
    }, [layers.commonFilters, goToFirstPage]);


    const columns = useMemo(() => [
        {
            accessorKey: 'station',
            cell: props => <StationCol station={props.row?.original} />,
            header: () => <span>{t("stationsTable.station")}</span>,
            enableSorting: true,
            meta: { width: "20rem" }
        },
        {
            accessorKey: 'code',
            cell: props => <StationCode station={props?.row?.original } />,
            header: () => <span>{t("stationsTable.code")}</span>,
            enableSorting: true,
            meta: { width: "5rem", align: "center" }
        },
        {
            accessorKey: 'elevation',
            cell: info => info.getValue() !== null ? `mt. ${info.getValue()}` : "",
            header: () => <span>{t("stationsTable.elevation")}</span>,
            enableSorting: true,
            meta: { width: "6rem", align: "center" }
        },
        {
            accessorKey: 'locationName',
            cell: info => info.getValue(),
            header: () => <span>{t("stationsTable.locationName")}</span>,
            enableSorting: true,
            meta: { width: "20rem", align: "left" }
        },
        {
            accessorKey: 'microZones',
            cell: info => info.getValue()?.[0]?.code,
            header: () => <span>{t("stationsTable.microZone")}</span>,
            enableSorting: false,
            meta: { width: "7rem", align: "center" }
        },
        {
            accessorKey: 'acceptMod1',
            cell: info => FlagIcon(info.getValue()),
            header: () => <span>{t("stationsTable.acceptMod1")}</span>,
            enableSorting: true,
            meta: { width: "4rem", align: "center" }
        },
        {
            accessorKey: 'acceptMod23',
            cell: info => FlagIcon(info.getValue()),
            header: () => <span>{t("stationsTable.acceptMod23")}</span>,
            enableSorting: true,
            meta: { width: "4rem", align: "center" }
        },
        {
            accessorKey: 'visibility',
            cell: info => t(`enums:stationVisibility.${info.getValue()}`),
            header: () => <span>{t("stationsTable.visibility")}</span>,
            enableSorting: false,
            meta: { width: "7rem", align: "center" }
        },
        {
            accessorKey: 'activeStatus',
            cell: props => <ActiveStatus station={props.row.original} />,
            header: () => <span>{t("stationsTable.activeStatus")}</span>,
            enableSorting: false,
            meta: { width: "6rem", align: "center" }
        },
    ], [t]);


    const table = useReactTable({
        data: rows,
        columns,
        pageCount,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
    });


    if (isLoading) return null;

    return (
        <MDBox>
            <FeatureSelectionChips
                selectedFeatures={selectedFeatures}
                onDeSelectFeature={handleFeatureDeSelect}
                sx={{ mb: 2, px: 2 }}
            />
            <DataTable table={table} showTotalEntries={false} />
        </MDBox>
    );
};

export default StationsTable;
